import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useAccessToken,
  useLink,
  useButtonId,
  useQrCode,
  useCryptoOnboardingComplete,
  useStripeOnboardingStatus,
  useSetLink,
  useSetButtonId,
  useSetQrCode
} from '../state';
import PageLayout from './PageLayout';
import Link from './Link';
import ButtonCode from './ButtonCode';
import QrCode from './QrCode';
import CopyButton from './common/CopyButton';
import DownloadButton from './common/DownloadButton';
import SetTip from './SetTip';
import Sidebar from './Sidebar';
import CustomizeButton from './common/CustomizeButton';
import { getUserData, getButtonId } from '../services';
import { useLocation } from 'react-router-dom';

const AccountContent = () => {
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const accessToken = useAccessToken();
  const link = useLink();
  const buttonId = useButtonId();
  const qrCode = useQrCode();
  const cryptoOnboardingComplete = useCryptoOnboardingComplete();
  const stripeOnboardingStatus = useStripeOnboardingStatus();
  const setLink = useSetLink();
  const setButtonId = useSetButtonId();
  const setQrCode = useSetQrCode();

  const [plainButtonCode, setPlainButtonCode] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  const handleCompleteOnboarding = () => {
    navigate('/stripe-onboarding')
  }

  useEffect(() => {
    if (!accessToken) {
      navigate('/');
    }
  }, [accessToken]);

  useEffect(() => {
    if (location.pathname === '/') {
      window.history.replaceState(null, '', '/account');
    }
  }, [location.pathname]);

  useEffect(() => {
    const baseUrl = process.env.REACT_APP_BASE_URL;
    const getUserDetails = async () => {
      try {
        const { linkId, qrCode } = await getUserData(['linkId', 'qrCode']);
        const buttonId = await getButtonId(linkId);

        setLink(`${baseUrl}/${linkId}`);
        setButtonId(buttonId);
        setQrCode(qrCode);
      } catch (error) {
        console.error('Error getting user data: ', error);
      }
    }
    getUserDetails();
  }, [accessToken]);

  useEffect(() => {
    if (buttonId) {
      setPlainButtonCode(`<script src="${apiBaseUrl}/button-loader.js" id="${buttonId}" async></script>`);
    }
  }, [buttonId]);

  return (
    <div className="flex md:h-[calc(100vh-3rem)]">
      {/* Sidebar - only visible on desktop */}
      <aside className="hidden md:block">
        <Sidebar />
      </aside>

      {/* Main content - adjust margin based on screen size */}
      <div className="page-background flex-1 md:ml-48 p-4 mb-12 relative">
        <div className="max-w-6xl mx-auto pt-12">
          <h1 className="sr-only">Account</h1>
          {(stripeOnboardingStatus === 'complete' || cryptoOnboardingComplete) ? (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Payment methods column */}
                <div className="bg-white rounded-lg md:col-span-2 space-y-4 p-6 max-w-3xl mx-auto">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-2 md:gap-4 items-center">
                    <div className="font-semibold text-black md:text-right md:pr-4 flex items-center md:justify-end self-center">Payment link:</div>
                    <div className="md:col-span-3 flex space-x-3">
                      <Link />
                      <div className="w-5 mt-[10px]">
                        <CopyButton content={link} aria-label="Copy payment link" />
                        <CustomizeButton link={'/customize'} targetTab="page" aria-label="Customize payment page" />
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-4 gap-2 md:gap-4 md:hidden">
                    <div className="font-semibold text-black md:text-right md:pr-4 my-1.5">QR code:</div>
                    <div className="md:col-span-3 flex space-x-3">
                      <QrCode maxHeight="600px" qrCode={qrCode} />
                      <div className="mt-[10px]">
                        <DownloadButton qrCode={qrCode} aria-label="Download QR code" />
                        <CustomizeButton link={'/customize'} className="mt-[8px]" targetTab="qrcode" aria-label="Customize QR code" />
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-4 gap-2 md:gap-4">
                    <div className="font-semibold text-black md:text-right md:pr-4 my-1.5">Button code:</div>
                    <div className="md:col-span-3 flex space-x-3">
                      <ButtonCode id={buttonId} />
                      <div className="w-5 mt-[10px]">
                        <CopyButton content={plainButtonCode} aria-label="Copy button code" />
                        <CustomizeButton link={'/customize'} targetTab="button" aria-label="Customize button" />
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-4 gap-2 md:gap-4 max-md:hidden">
                    <div className="font-semibold text-black md:text-right md:pr-4 my-1.5">QR code:</div>
                    <div className="md:col-span-3 flex space-x-3">
                      <QrCode maxHeight="300px" qrCode={qrCode} />
                      <div className="mt-[10px]">
                        <DownloadButton qrCode={qrCode} aria-label="Download QR code" />
                        <CustomizeButton link={'/customize'} className="mt-[8px]" targetTab="qrcode" aria-label="Customize QR code" />
                      </div>
                    </div>
                  </div>


                </div>

                {/* Tip settings column */}
                <div className="space-y-4">
                  <SetTip />
                </div>
              </div>
            </div>) : (
            <div className="text-white text-center">
              <p>You need to complete the onboarding process to access your payment link, button code, and QR code.</p>
              <p>Please complete the onboarding to unlock these features.</p>
              <button onClick={handleCompleteOnboarding} className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded">Complete onboarding</button>
            </div>
          )}
        </div>
        {/* <div className="mt-8">
          <Logout className="absolute top-4 right-4" />
        </div> */}
      </div>
    </div>
  );
}

const Account = () => {
  return (
    <PageLayout showLogout={false} showProfileIcon={true}>
      <AccountContent />
    </PageLayout>
  )
}

export default Account;